// components/document-settings/PreviewTab.jsx
// Preview e test generazione documenti

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Eye, Download, FileText, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

export const PreviewTab = ({
  templates,
  estimates,
  selectedTemplateForPreview,
  setSelectedTemplateForPreview,
  selectedEstimateForPreview,
  setSelectedEstimateForPreview
}) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const templatesWithDocx = templates.filter(t => t.source_docx_path);

  const getAuthHeaders = () => ({
    withCredentials: true,
    'Content-Type': 'application/json'
  });

  const handleGeneratePreview = async () => {
    if (!selectedTemplateForPreview || !selectedEstimateForPreview) {
      toast.error('Seleziona un template e un preventivo');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/estimates/${selectedEstimateForPreview}/pdf/preview?template_id=${selectedTemplateForPreview}`,
        {
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        toast.success('Preview generato con successo');
      } else {
        toast.error('Errore nella generazione del preview');
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Errore durante la generazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPreview = () => {
    if (previewUrl) {
      const link = document.createElement('a');
      link.href = previewUrl;
      link.download = `preview_${selectedEstimateForPreview}.pdf`;
      link.click();
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Preview & Test</h3>
        <p className="text-sm text-gray-500">Genera un'anteprima dei documenti con i template configurati</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configurazione Test</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">Template</label>
              <Select 
                value={selectedTemplateForPreview || ''}
                onValueChange={setSelectedTemplateForPreview}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona template..." />
                </SelectTrigger>
                <SelectContent>
                  {templatesWithDocx.length === 0 ? (
                    <SelectItem value="_none" disabled>
                      Nessun template con file DOCX
                    </SelectItem>
                  ) : (
                    templatesWithDocx.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.template_name} ({t.template_code})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Preventivo di Test</label>
              <Select 
                value={selectedEstimateForPreview || ''}
                onValueChange={setSelectedEstimateForPreview}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona preventivo..." />
                </SelectTrigger>
                <SelectContent>
                  {estimates.length === 0 ? (
                    <SelectItem value="_none" disabled>
                      Nessun preventivo disponibile
                    </SelectItem>
                  ) : (
                    estimates.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.estimate_number} - {e.customer_name || 'N/D'}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleGeneratePreview}
                disabled={loading || !selectedTemplateForPreview || !selectedEstimateForPreview}
                className="bg-[#FF7819] hover:bg-[#E56A10] flex-1"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-1.5" />
                    Genera Preview
                  </>
                )}
              </Button>
              {previewUrl && (
                <Button
                  variant="outline"
                  onClick={handleDownloadPreview}
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Scarica
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Anteprima
            </CardTitle>
          </CardHeader>
          <CardContent>
            {previewUrl ? (
              <div className="space-y-3">
                <div className="border rounded-lg overflow-hidden bg-gray-100">
                  <iframe
                    src={previewUrl}
                    className="w-full h-[500px]"
                    title="PDF Preview"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(previewUrl, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Apri in nuova finestra
                  </Button>
                </div>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed rounded-lg">
                <FileText className="w-12 h-12 mb-3" />
                <p>Seleziona un template e un preventivo</p>
                <p className="text-sm">poi clicca "Genera Preview"</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900">Come funziona il preview</h4>
              <p className="text-sm text-blue-700 mt-1">
                Il sistema utilizza il template DOCX selezionato e lo popola con i dati del preventivo scelto.
                I placeholder vengono sostituiti con i valori reali, permettendoti di verificare che il layout
                sia corretto prima di usarlo in produzione.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PreviewTab;
